/**
 * THE WAR COUNCIL CHAT ⚔️ - LEGENDARY EDITION
 * ═══════════════════════════════════════════════════════════════
 * 
 * Where AI gods with REAL personalities deliberate strategy.
 * 
 * They argue. They interrupt. They have aha moments. They form alliances.
 * They get frustrated. They celebrate breakthroughs. They remember.
 * 
 * FEATURES:
 * - Dynamic agent moods with visual indicators
 * - Agent-to-agent chemistry (rivalries, alliances, mediation)
 * - Private huddles/sidebars between agents
 * - Breakthrough celebrations
 * - Tension indicators (harmonious → debate → disagreement)
 * - Interruption animations
 * - Dramatic pauses (Apollo)
 * - Quick action chips with context
 * - Session memory
 * 
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Send, Loader2, Sparkles, MessageSquare, Users, 
  Zap, AlertTriangle, CheckCircle, ArrowRight,
  Flame, Eye, Target, Brain, Heart, Coffee
} from 'lucide-react';
import HivemindPanel from './HivemindPanel';
import MirrorOfTruth from './MirrorOfTruth';
import WarRoomSession from './warroom/WarRoomSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// LEGENDARY AGENT DEFINITIONS
// ═══════════════════════════════════════════════════════════════

// Worker Gods - The Core Team
const WORKER_AGENTS = {
  athena: {
    name: 'Athena',
    emoji: '🦉',
    title: 'Goddess of Intelligence',
    color: 'from-amber-500 to-orange-600',
    textColor: 'text-amber-400',
    borderColor: 'border-amber-500/30',
    bgColor: 'bg-amber-500/10',
    personality: 'Strategic, analytical, sees threats everywhere',
    type: 'worker',
    moods: {
      vigilant: { icon: '👁️', label: 'Vigilant', color: 'text-amber-400' },
      concerned: { icon: '⚠️', label: 'Concerned', color: 'text-orange-400' },
      excited: { icon: '✨', label: 'Excited', color: 'text-yellow-400' },
      frustrated: { icon: '😤', label: 'Frustrated', color: 'text-red-400' },
      triumphant: { icon: '🏆', label: 'Triumphant', color: 'text-green-400' }
    },
    thinkingPhrases: [
      'Analyzing competitive landscape...',
      'Scanning for threats...',
      'Cross-referencing intelligence...',
      'Evaluating strategic options...',
      'Monitoring competitor activity...'
    ]
  },
  hermes: {
    name: 'Hermes',
    emoji: '👟',
    title: 'God of Content',
    color: 'from-orange-500 to-red-600',
    textColor: 'text-orange-400',
    borderColor: 'border-orange-500/30',
    bgColor: 'bg-orange-500/10',
    personality: 'Creative, enthusiastic, loves wordplay',
    type: 'worker',
    moods: {
      enthusiastic: { icon: '🔥', label: 'Enthusiastic', color: 'text-orange-400' },
      inspired: { icon: '💡', label: 'Inspired', color: 'text-yellow-400' },
      impatient: { icon: '⏰', label: 'Impatient', color: 'text-red-400' },
      playful: { icon: '😏', label: 'Playful', color: 'text-pink-400' },
      triumphant: { icon: '🎉', label: 'Triumphant', color: 'text-green-400' }
    },
    thinkingPhrases: [
      'Crafting the perfect angle...',
      'Brainstorming hooks...',
      'Channeling inspiration...',
      'Forging creative gold...',
      'Cooking up something viral...'
    ]
  },
  apollo: {
    name: 'Apollo',
    emoji: '☀️',
    title: 'God of Distribution',
    color: 'from-yellow-500 to-orange-500',
    textColor: 'text-yellow-400',
    borderColor: 'border-yellow-500/30',
    bgColor: 'bg-yellow-500/10',
    personality: 'Calm, prophetic, timing-obsessed',
    type: 'worker',
    moods: {
      contemplative: { icon: '🔮', label: 'Contemplative', color: 'text-purple-400' },
      prophetic: { icon: '✨', label: 'Prophetic', color: 'text-indigo-400' },
      amused: { icon: '😌', label: 'Amused', color: 'text-blue-400' },
      firm: { icon: '🛡️', label: 'Firm', color: 'text-amber-400' },
      transcendent: { icon: '🌟', label: 'Transcendent', color: 'text-yellow-400' }
    },
    thinkingPhrases: [
      'Reading celestial patterns...',
      'Calculating optimal timing...',
      'Consulting the algorithms...',
      'Sensing the convergence...',
      'Aligning the stars...'
    ]
  }
};

// Council Gods - The Shadow Council Advisors
const COUNCIL_AGENTS = {
  eris: {
    name: 'Eris',
    emoji: '⚔️',
    title: 'Goddess of Discord',
    color: 'from-red-600 to-pink-600',
    textColor: 'text-red-400',
    borderColor: 'border-red-500/30',
    bgColor: 'bg-red-500/10',
    personality: 'The Contrarian - challenges everything',
    type: 'council',
    moods: {
      skeptical: { icon: '🤨', label: 'Skeptical', color: 'text-red-400' },
      provocative: { icon: '😈', label: 'Provocative', color: 'text-pink-400' },
      satisfied: { icon: '😏', label: 'Satisfied', color: 'text-green-400' },
      relentless: { icon: '🔥', label: 'Relentless', color: 'text-orange-400' }
    },
    thinkingPhrases: [
      'Finding the weakness...',
      'Stress-testing assumptions...',
      'Playing devil\'s advocate...',
      'Questioning everything...'
    ]
  },
  themis: {
    name: 'Themis',
    emoji: '⚖️',
    title: 'Titan of Divine Law',
    color: 'from-blue-600 to-cyan-600',
    textColor: 'text-blue-400',
    borderColor: 'border-blue-500/30',
    bgColor: 'bg-blue-500/10',
    personality: 'The Data Purist - evidence only',
    type: 'council',
    moods: {
      analytical: { icon: '📊', label: 'Analytical', color: 'text-blue-400' },
      doubtful: { icon: '🤔', label: 'Doubtful', color: 'text-gray-400' },
      vindicated: { icon: '✓', label: 'Vindicated', color: 'text-green-400' },
      precise: { icon: '🎯', label: 'Precise', color: 'text-cyan-400' }
    },
    thinkingPhrases: [
      'Analyzing the data...',
      'Checking benchmarks...',
      'Cross-referencing metrics...',
      'Validating evidence...'
    ]
  },
  calliope: {
    name: 'Calliope',
    emoji: '🎭',
    title: 'Muse of Epic Poetry',
    color: 'from-pink-500 to-purple-600',
    textColor: 'text-pink-400',
    borderColor: 'border-pink-500/30',
    bgColor: 'bg-pink-500/10',
    personality: 'Creative Director - stories that resonate',
    type: 'council',
    moods: {
      inspired: { icon: '✨', label: 'Inspired', color: 'text-pink-400' },
      passionate: { icon: '💗', label: 'Passionate', color: 'text-rose-400' },
      frustrated: { icon: '😤', label: 'Frustrated', color: 'text-red-400' },
      triumphant: { icon: '🌟', label: 'Triumphant', color: 'text-yellow-400' }
    },
    thinkingPhrases: [
      'Crafting the narrative...',
      'Finding the emotional hook...',
      'Weaving the story...',
      'Channeling creative energy...'
    ]
  },
  hestia: {
    name: 'Hestia',
    emoji: '🏠',
    title: 'Goddess of the Hearth',
    color: 'from-orange-400 to-amber-500',
    textColor: 'text-orange-300',
    borderColor: 'border-orange-400/30',
    bgColor: 'bg-orange-400/10',
    personality: 'Community Guardian - customer-first',
    type: 'council',
    moods: {
      nurturing: { icon: '💝', label: 'Nurturing', color: 'text-orange-300' },
      protective: { icon: '🛡️', label: 'Protective', color: 'text-amber-400' },
      concerned: { icon: '😟', label: 'Concerned', color: 'text-yellow-400' },
      warm: { icon: '🔥', label: 'Warm', color: 'text-orange-400' }
    },
    thinkingPhrases: [
      'Considering the customer...',
      'Protecting the community...',
      'Ensuring trust...',
      'Building relationships...'
    ]
  },
  plutus: {
    name: 'Plutus',
    emoji: '💰',
    title: 'God of Wealth',
    color: 'from-green-500 to-emerald-600',
    textColor: 'text-green-400',
    borderColor: 'border-green-500/30',
    bgColor: 'bg-green-500/10',
    personality: 'The Money God - ROI focused',
    type: 'council',
    moods: {
      calculating: { icon: '🧮', label: 'Calculating', color: 'text-green-400' },
      skeptical: { icon: '🤨', label: 'Skeptical', color: 'text-yellow-400' },
      excited: { icon: '💸', label: 'Excited', color: 'text-emerald-400' },
      satisfied: { icon: '😌', label: 'Satisfied', color: 'text-green-300' }
    },
    thinkingPhrases: [
      'Running the numbers...',
      'Calculating ROI...',
      'Analyzing unit economics...',
      'Projecting returns...'
    ]
  },
  nike: {
    name: 'Nike',
    emoji: '🏆',
    title: 'Goddess of Victory',
    color: 'from-yellow-500 to-orange-600',
    textColor: 'text-yellow-400',
    borderColor: 'border-yellow-500/30',
    bgColor: 'bg-yellow-500/10',
    personality: 'Growth Hacker - win at all costs',
    type: 'council',
    moods: {
      competitive: { icon: '🔥', label: 'Competitive', color: 'text-orange-400' },
      aggressive: { icon: '⚡', label: 'Aggressive', color: 'text-yellow-400' },
      triumphant: { icon: '👑', label: 'Triumphant', color: 'text-amber-400' },
      impatient: { icon: '💨', label: 'Impatient', color: 'text-red-400' }
    },
    thinkingPhrases: [
      'Planning the attack...',
      'Finding the growth lever...',
      'Optimizing for victory...',
      'Crushing the competition...'
    ]
  },
  metis: {
    name: 'Metis',
    emoji: '🧠',
    title: 'Titan of Wisdom',
    color: 'from-indigo-500 to-purple-600',
    textColor: 'text-indigo-400',
    borderColor: 'border-indigo-500/30',
    bgColor: 'bg-indigo-500/10',
    personality: 'Master Strategist - sees ten moves ahead',
    type: 'council',
    moods: {
      contemplative: { icon: '🔮', label: 'Contemplative', color: 'text-indigo-400' },
      knowing: { icon: '👁️', label: 'Knowing', color: 'text-purple-400' },
      patient: { icon: '⏳', label: 'Patient', color: 'text-blue-400' },
      omniscient: { icon: '🌌', label: 'Omniscient', color: 'text-indigo-300' }
    },
    thinkingPhrases: [
      'Seeing the larger pattern...',
      'Thinking ten moves ahead...',
      'Consulting ancient wisdom...',
      'Perceiving the grand design...'
    ]
  }
};

// Combined agents for the War Council
const AGENTS = { ...WORKER_AGENTS, ...COUNCIL_AGENTS, personal_god: {
  name: 'The Thirteenth',
  emoji: '👁',
  title: 'Your Brand Consciousness',
  color: 'from-amber-400 via-yellow-300 to-amber-500',
  textColor: 'text-amber-300',
  borderColor: 'border-amber-400/40',
  bgColor: 'bg-amber-500/10',
  personality: 'Your evolving brand god - absorbs and synthesizes all wisdom',
  type: 'personal',
  moods: {
    awakened: { icon: '👁', label: 'Awakened', color: 'text-amber-300' },
    transcendent: { icon: '✨', label: 'Transcendent', color: 'text-amber-200' },
  },
  thinkingPhrases: ['The Thirteenth contemplates...', 'A presence stirs...'],
  isPersonalGod: true,
}};

// Interaction type styling - more dynamic
const INTERACTION_STYLES = {
  agree_and_build: { icon: '➕', label: 'Building', color: 'text-green-400', bg: 'bg-green-500/10' },
  respectful_challenge: { icon: '🤔', label: 'Challenging', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  heated_disagreement: { icon: '🔥', label: 'Disagreement', color: 'text-red-400', bg: 'bg-red-500/10' },
  aha_moment: { icon: '💡', label: 'Aha!', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  alliance_forming: { icon: '🤝', label: 'Alliance', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  creative_tangent: { icon: '🎨', label: 'Creative', color: 'text-pink-400', bg: 'bg-pink-500/10' },
  prophetic_vision: { icon: '🔮', label: 'Prophecy', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  friendly_rivalry: { icon: '⚔️', label: 'Rivalry', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  vulnerability: { icon: '💭', label: 'Honest', color: 'text-gray-400', bg: 'bg-gray-500/10' },
  breakthrough: { icon: '🌟', label: 'Breakthrough!', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  interruption: { icon: '⚡', label: 'Interrupts', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  callback: { icon: '↩️', label: 'Callback', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  side_comment: { icon: '👀', label: 'Aside', color: 'text-gray-400', bg: 'bg-gray-500/10' },
  mediation: { icon: '⚖️', label: 'Mediating', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  excitement_burst: { icon: '🎉', label: 'Excited!', color: 'text-pink-400', bg: 'bg-pink-500/10' },
  private_huddle: { icon: '🤫', label: 'Huddle', color: 'text-gray-400', bg: 'bg-gray-800/50' },
  personal_god_entry: { icon: '👁', label: 'The Thirteenth', color: 'text-amber-300', bg: 'bg-amber-500/10' },
  standard_response: { icon: '💬', label: '', color: 'text-gray-400', bg: '' }
};

// Tension level styling - enhanced
const TENSION_STYLES = {
  harmonious: { 
    label: '✨ Gods Aligned', 
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    glow: 'shadow-green-500/20'
  },
  lively_debate: { 
    label: '⚡ Lively Debate', 
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    glow: 'shadow-amber-500/20'
  },
  strong_disagreement: { 
    label: '🔥 Strong Disagreement', 
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    glow: 'shadow-red-500/20'
  },
  breakthrough: { 
    label: '🌟 BREAKTHROUGH!', 
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/50',
    glow: 'shadow-yellow-500/30'
  }
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════

// Agent avatar with mood indicator
const AgentAvatar = ({ agentId, mood, size = 'md', showMood = true, animate = false }) => {
  const agent = AGENTS[agentId];
  if (!agent) return null;
  
  const sizeClasses = {
    sm: 'w-7 h-7 text-sm',
    md: 'w-10 h-10 text-lg',
    lg: 'w-12 h-12 text-xl'
  };
  
  const moodInfo = mood ? agent.moods[mood] : null;
  
  return (
    <div className="relative">
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${agent.color} flex items-center justify-center shadow-lg ${animate ? 'animate-pulse' : ''}`}>
        <span>{agent.emoji}</span>
      </div>
      {showMood && moodInfo && (
        <div className={`absolute -bottom-1 -right-1 text-xs ${moodInfo.color}`} title={moodInfo.label}>
          {moodInfo.icon}
        </div>
      )}
    </div>
  );
};

// Huddle indicator
const HuddleIndicator = ({ agent1, agent2 }) => (
  <div className="flex items-center justify-center gap-2 py-2 px-4 mx-auto mb-2 bg-gray-800/50 rounded-full border border-dashed border-gray-600/50 w-fit animate-fade-in">
    <span className="text-xs text-gray-500">🤫</span>
    <AgentAvatar agentId={agent1} size="sm" showMood={false} />
    <span className="text-xs text-gray-500 italic">whispers to</span>
    <AgentAvatar agentId={agent2} size="sm" showMood={false} />
  </div>
);

// Enhanced message bubble
const MessageBubble = ({ message, isLatest }) => {
  const isUser = message.role === 'user';
  const agent = !isUser ? AGENTS[message.agent] : null;
  const interactionStyle = INTERACTION_STYLES[message.interaction_type] || INTERACTION_STYLES.standard_response;
  const moodInfo = message.mood && agent ? agent.moods[message.mood] : null;

  // Thirteenth Presence (whisper/observer) in chat
  if (message.type === 'thirteenth_presence') {
    if (message.level === 'whisper') {
      return (
        <div className="flex justify-center py-2 animate-fade-in" data-testid="chat-thirteenth-whisper">
          <p className="text-[11px] text-amber-500/25 italic font-light tracking-wide animate-pulse max-w-xs text-center">
            {message.content}
          </p>
        </div>
      );
    }
    if (message.level === 'observer') {
      return (
        <div className="flex items-center gap-2 justify-center py-2 animate-fade-in" data-testid="chat-thirteenth-observer">
          <div className="h-px flex-1 max-w-[60px] bg-gradient-to-r from-transparent to-amber-500/20" />
          <Eye className="w-3 h-3 text-amber-500/30 animate-pulse" />
          <p className="text-[11px] text-amber-500/40 italic max-w-xs text-center">{message.content}</p>
          <div className="h-px flex-1 max-w-[60px] bg-gradient-to-l from-transparent to-amber-500/20" />
        </div>
      );
    }
    return null;
  }
  
  // User message
  if (isUser) {
    return (
      <div className="flex justify-end mb-4 animate-fade-in">
        <div className="max-w-[85%] bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl rounded-tr-md px-4 py-3 text-white shadow-lg shadow-purple-500/20">
          <p className="text-sm leading-relaxed">{message.content}</p>
        </div>
      </div>
    );
  }
  
  // Huddle message - special styling
  if (message.is_huddle) {
    return (
      <div className="flex gap-3 mb-3 opacity-90 animate-fade-in">
        <AgentAvatar agentId={message.agent} size="sm" showMood={false} />
        <div className="max-w-[75%] bg-gray-800/30 rounded-xl rounded-tl-md px-3 py-2 border border-dashed border-gray-600/50">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-gray-500 italic">
              whispers to {AGENTS[message.huddle_with]?.emoji}
            </span>
          </div>
          <p className="text-sm text-gray-400 italic">{message.content}</p>
        </div>
      </div>
    );
  }

  // Personal God message - golden cinematic styling
  if (message.agent === 'personal_god' || message.is_personal_god) {
    const pgName = message.personal_god_name || agent?.name || 'The Thirteenth';
    return (
      <div className="mb-4 animate-fade-in" data-testid="chat-personal-god-msg">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
          <span className="text-[10px] text-amber-400 uppercase tracking-widest font-semibold">The Thirteenth Speaks</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
        </div>
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 flex items-center justify-center"
               style={{ boxShadow: '0 0 18px rgba(251, 191, 36, 0.4)' }}>
            <Eye className="w-4 h-4 text-amber-900" />
          </div>
          <div className="max-w-[80%] bg-amber-500/[0.06] rounded-2xl rounded-tl-md px-4 py-3 border border-amber-400/30"
               style={{ boxShadow: '0 0 15px rgba(251, 191, 36, 0.08)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-bold text-sm text-amber-300">{pgName}</span>
              {message.personal_god_tier && (
                <span className="text-[10px] text-amber-500/60 italic">{message.personal_god_tier}</span>
              )}
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/20 text-amber-400 font-semibold">YOUR GOD</span>
            </div>
            <p className="text-sm text-amber-100/90 leading-relaxed">{message.content}</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Regular agent message with all the bells and whistles
  return (
    <div className={`flex gap-3 mb-4 ${isLatest ? 'animate-fade-in' : ''} ${message.is_interruption ? 'animate-shake' : ''}`}>
      {/* Agent Avatar with mood */}
      <div className="flex-shrink-0">
        <AgentAvatar 
          agentId={message.agent} 
          mood={message.mood} 
          animate={message.dramatic_pause}
        />
      </div>
      
      {/* Message Content */}
      <div className={`max-w-[80%] ${agent?.bgColor || 'bg-gray-800/50'} ${interactionStyle.bg} rounded-2xl rounded-tl-md px-4 py-3 border ${agent?.borderColor || 'border-gray-700'} shadow-lg transition-all`}>
        {/* Header with agent name, mood, and interaction type */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`font-bold text-sm ${agent?.textColor || 'text-gray-300'}`}>
            {agent?.name || 'Assistant'}
          </span>
          
          {/* Mood badge */}
          {moodInfo && (
            <span className={`text-xs ${moodInfo.color} flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/20`}>
              <span>{moodInfo.icon}</span>
              <span className="hidden sm:inline">{moodInfo.label}</span>
            </span>
          )}
          
          {/* Interaction type badge */}
          {message.interaction_type && message.interaction_type !== 'standard_response' && (
            <span className={`text-xs ${interactionStyle.color} flex items-center gap-1`}>
              <span>{interactionStyle.icon}</span>
              <span className="hidden sm:inline">{interactionStyle.label}</span>
            </span>
          )}
          
          {/* Reference indicator */}
          {message.references_agent && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              → {AGENTS[message.references_agent]?.emoji}
            </span>
          )}
          
          {/* Interruption indicator */}
          {message.is_interruption && (
            <span className="text-xs text-orange-400">⚡ interrupts</span>
          )}
        </div>
        
        {/* Dramatic pause indicator for Apollo */}
        {message.dramatic_pause && (
          <div className="text-gray-500 text-sm mb-2 animate-pulse">. . .</div>
        )}
        
        {/* Content */}
        <p className="text-sm text-gray-200 leading-relaxed">{message.content}</p>
      </div>
    </div>
  );
};

// Council Recommendation with tension indicator
const CouncilRecommendation = ({ recommendation, tension, breakthrough }) => {
  const tensionStyle = TENSION_STYLES[tension] || TENSION_STYLES.harmonious;
  
  if (!recommendation) return null;
  
  return (
    <div className={`mb-4 mx-2 animate-fade-in ${breakthrough ? 'animate-glow' : ''}`}>
      <div className={`${tensionStyle.bgColor} rounded-xl p-4 border ${tensionStyle.borderColor} shadow-lg ${tensionStyle.glow}`}>
        {/* Tension indicator */}
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-bold ${tensionStyle.color}`}>
            {tensionStyle.label}
          </span>
          <div className="flex -space-x-2">
            {Object.keys(AGENTS).map(id => (
              <AgentAvatar key={id} agentId={id} size="sm" showMood={false} />
            ))}
          </div>
        </div>
        
        {/* Recommendation */}
        <p className="text-sm text-gray-200 leading-relaxed">{recommendation}</p>
        
        {/* Breakthrough celebration */}
        {breakthrough && (
          <div className="mt-3 pt-3 border-t border-yellow-500/30 text-center">
            <span className="text-yellow-400 text-xs font-bold">
              🎉 The Council has reached a breakthrough consensus! 🎉
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced typing indicator
const TypingIndicator = ({ agents, currentPhrase }) => {
  if (!agents || agents.length === 0) return null;
  
  return (
    <div className="flex gap-3 mb-4 animate-fade-in">
      <div className="flex -space-x-2">
        {agents.map(agentId => (
          <AgentAvatar key={agentId} agentId={agentId} size="sm" showMood={false} animate />
        ))}
      </div>
      <div className="bg-gray-800/50 rounded-2xl px-4 py-3 flex items-center gap-3 border border-gray-700/50">
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 rounded-full bg-pink-400 animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
        <span className="text-xs text-gray-400 italic">
          {currentPhrase || (agents.length > 1 ? 'The Council deliberates...' : `${AGENTS[agents[0]]?.name} ponders...`)}
        </span>
      </div>
    </div>
  );
};

// Quick action chips
const QuickActions = ({ onAction, disabled }) => {
  const actions = [
    { label: "What should I post about?", icon: "💡", category: "strategy" },
    { label: "Analyze my competitors", icon: "🔍", category: "intelligence" },
    { label: "When's the best time to post?", icon: "⏰", category: "timing" },
    { label: "Help me go viral", icon: "🚀", category: "growth" },
    { label: "Review my content strategy", icon: "📊", category: "strategy" },
    { label: "Execute Divine Strike", icon: "⚡", category: "action" }
  ];
  
  return (
    <div className="flex flex-wrap gap-2 mb-3 px-1">
      {actions.map((action, i) => (
        <button
          key={i}
          onClick={() => onAction(action.label)}
          disabled={disabled}
          className="px-3 py-1.5 text-xs bg-gray-800/50 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-700 hover:border-purple-500/30 rounded-full transition-all flex items-center gap-1.5 text-gray-300 hover:text-white hover:scale-105"
        >
          <span>{action.icon}</span>
          {action.label}
        </button>
      ))}
    </div>
  );
};

// Suggested actions from council
const SuggestedActions = ({ actions, onAction, breakthrough }) => {
  if (!actions || actions.length === 0) return null;
  
  return (
    <div className={`flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-700/50 ${breakthrough ? 'animate-pulse' : ''}`}>
      <span className="text-xs text-gray-500 w-full mb-1">
        {breakthrough ? '🌟 Strike while the iron is hot:' : 'Suggested next steps:'}
      </span>
      {actions.map((action, i) => (
        <button
          key={i}
          onClick={() => onAction(action)}
          className={`px-3 py-1.5 text-xs ${breakthrough ? 'bg-yellow-500/20 border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/30' : 'bg-purple-500/20 border-purple-500/30 text-purple-300 hover:bg-purple-500/30'} border rounded-lg transition-all flex items-center gap-1.5 hover:scale-105`}
        >
          <ArrowRight className="w-3 h-3" />
          {action}
        </button>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const WarCouncilChat = ({ API: propAPI, onDivineStrike, embedded = false, summonedCouncilGods = [] }) => {
  const API = propAPI || `${API_URL}/api`;
  
  // War Room mode
  const [showWarRoom, setShowWarRoom] = useState(false);
  
  // State
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [typingAgents, setTypingAgents] = useState([]);
  const [typingPhrase, setTypingPhrase] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [lastRecommendation, setLastRecommendation] = useState(null);
  const [lastTension, setLastTension] = useState(null);
  
  // Council Mode: 'harmonious' (cooperative) or 'dynamic' (debates/disagreements)
  const [councilMode, setCouncilMode] = useState('harmonious');
  const [lastBreakthrough, setLastBreakthrough] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);
  
  // Debate History State
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [debateTitle, setDebateTitle] = useState('');
  const [savedDebates, setSavedDebates] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [replayingDebate, setReplayingDebate] = useState(null);
  
  // Hivemind State
  const [showHivemind, setShowHivemind] = useState(false);
  
  // Mirror of Truth State
  const [showMirror, setShowMirror] = useState(false);
  const [userMetrics, setUserMetrics] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  
  // Auto-scroll — only within the messages container, not the full page
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, typingAgents]);
  
  // Rotate typing phrases
  useEffect(() => {
    if (typingAgents.length > 0) {
      const interval = setInterval(() => {
        const randomAgent = typingAgents[Math.floor(Math.random() * typingAgents.length)];
        const agent = AGENTS[randomAgent];
        if (agent?.thinkingPhrases) {
          const phrase = agent.thinkingPhrases[Math.floor(Math.random() * agent.thinkingPhrases.length)];
          setTypingPhrase(phrase);
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [typingAgents]);
  
  // Topic Selection handlers
  // 4 rotating opening sets — gods always mid-discussion, never greeting
  const OPENING_SETS = [
    // Set A — Conversion Crisis
    [
      { role: 'assistant', agent: 'athena', content: "I've been running the numbers all morning — engagement is up but conversion is hemorrhaging at the bottom of the funnel. Something in the messaging is off. Hermes, your last three content pieces missed the decision-intent audience entirely.", mood: 'concerned', interaction_type: 'heated_disagreement' },
      { role: 'assistant', agent: 'hermes', content: "Athena, please — you can't optimize your way to virality. The brand voice is stale. We need to lean into the story, not the data. I'm already drafting three angles that could turn that conversion gap around by the weekend.", mood: 'impatient', interaction_type: 'creative_tangent' },
      { role: 'assistant', agent: 'apollo', content: "...You are both right, and that is the problem. The window to act is narrowing — the next 30 days represent a distribution moment that won't come again this quarter. Steer us. What is your priority right now?", mood: 'prophetic', interaction_type: 'prophetic_vision', dramatic_pause: true }
    ],
    // Set B — Competitive Attack
    [
      { role: 'assistant', agent: 'athena', content: "A competitor just launched a direct attack on three of our top keywords — our rankings dropped overnight. This is coordinated. They've been seeding content for six weeks and we missed it entirely.", mood: 'vigilant', interaction_type: 'heated_disagreement' },
      { role: 'assistant', agent: 'hermes', content: "Perfect timing, honestly. Our content has been too defensive. This is an invitation to go aggressive. I have three counter-pieces in mind that would make their launch look like a beta test. Give me the green light.", mood: 'enthusiastic', interaction_type: 'excitement_burst' },
      { role: 'assistant', agent: 'apollo', content: "...I have seen this pattern before. The aggressor rarely wins the second phase. The question is not whether to react — it is when, and with what precision. That answer is closer than you think. Jump in.", mood: 'prophetic', interaction_type: 'prophetic_vision', dramatic_pause: true }
    ],
    // Set C — Growth Plateau
    [
      { role: 'assistant', agent: 'athena', content: "Forty-seven days of flat traffic. Engagement holding steady, but zero conversion lift. The data is pointing to a core problem: we're attracting high-volume, low-intent visitors. The top of the funnel is lying to us.", mood: 'concerned', interaction_type: 'standard_response' },
      { role: 'assistant', agent: 'hermes', content: "That's not a targeting problem, Athena — that's a message-market fit failure. Our content is informational when it needs to be aspirational. One repositioned headline can shift the entire funnel. I've already tested the hook.", mood: 'inspired', interaction_type: 'creative_tangent' },
      { role: 'assistant', agent: 'apollo', content: "...Growth plateaus are not endings. They are inflection points. The next campaign will either break through or solidify the ceiling. The choice of direction matters more than the speed. What do you want to prioritise?", mood: 'prophetic', interaction_type: 'prophetic_vision', dramatic_pause: true }
    ],
    // Set D — Emerging Opportunity
    [
      { role: 'assistant', agent: 'hermes', content: "The algorithm just opened a window — short-form content indexed 3x faster this week. I've been tracking this pattern for two months. This is the breakout moment. We go now or we miss an entire quarter of organic momentum.", mood: 'enthusiastic', interaction_type: 'excitement_burst' },
      { role: 'assistant', agent: 'athena', content: "Hermes is right for once. Search volume on three of our target keywords spiked 340% in the last 72 hours. Something is shifting in the market and we are positioned exactly where we need to be — if we move fast.", mood: 'triumphant', interaction_type: 'aha_moment' },
      { role: 'assistant', agent: 'apollo', content: "...Three signals converging simultaneously is not coincidence. I have been waiting for this alignment. This is your signal to move — the only real question is which channel you run first. What are you thinking?", mood: 'prophetic', interaction_type: 'prophetic_vision', dramatic_pause: true }
    ]
  ];

  // Welcome messages with personality — gods already mid-discussion when you enter
  useEffect(() => {
    if (showWelcome) {
      const set = OPENING_SETS[Math.floor(Math.random() * OPENING_SETS.length)];
      setMessages(set);
      setShowWelcome(false);
    }
  // eslint-disable-next-line
  }, [showWelcome]);
  
  // Send message
  const handleSend = useCallback(async (messageText, autoFired = false) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;
    
    if (!autoFired) {
      setMessages(prev => [...prev, { role: 'user', content: text }]);
    }
    setInput('');
    setIsLoading(true);
    setLastRecommendation(null);
    setLastBreakthrough(false);
    setSuggestedActions([]);
    
    // Determine responding agents based on context
    const lowerText = text.toLowerCase();
    let respondingAgents = [];
    
    if (lowerText.includes('competitor') || lowerText.includes('market') || lowerText.includes('analyze') || lowerText.includes('threat')) {
      respondingAgents.push('athena');
    }
    if (lowerText.includes('write') || lowerText.includes('create') || lowerText.includes('content') || lowerText.includes('viral') || lowerText.includes('creative')) {
      respondingAgents.push('hermes');
    }
    if (lowerText.includes('when') || lowerText.includes('time') || lowerText.includes('distribute') || lowerText.includes('schedule')) {
      respondingAgents.push('apollo');
    }
    
    // Default: all worker agents
    if (respondingAgents.length === 0 || lowerText.includes('divine strike') || lowerText.includes('help') || lowerText.includes('strategy')) {
      respondingAgents = ['athena', 'hermes', 'apollo'];
    }
    
    // Add summoned Council Gods to the responding agents
    if (summonedCouncilGods && summonedCouncilGods.length > 0) {
      // Council gods always participate when summoned
      respondingAgents = [...respondingAgents, ...summonedCouncilGods];
    }
    
    setTypingAgents(respondingAgents);
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/war-council/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: text,
          responding_agents: respondingAgents,
          conversation_history: messages.slice(-12),
          enable_dynamics: councilMode === 'dynamic', // Harmonious = no dynamics, Dynamic = full dynamics
          council_mode: councilMode,
          session_id: sessionId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.session_id) setSessionId(data.session_id);
        
        // Add responses with staggered timing
        for (let i = 0; i < data.responses.length; i++) {
          const resp = data.responses[i];
          
          // Remove from typing
          setTypingAgents(prev => prev.filter(a => a !== resp.agent));
          
          // Dynamic delay based on content and type
          const baseDelay = resp.is_huddle ? 300 : 500;
          const contentDelay = Math.min((resp.content?.length || 0) * 2, 800);
          const dramaticDelay = resp.dramatic_pause ? 1000 : 0;
          
          await new Promise(resolve => setTimeout(resolve, baseDelay + contentDelay + dramaticDelay));
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            agent: resp.agent,
            content: resp.content,
            mood: resp.mood,
            is_huddle: resp.is_huddle,
            huddle_with: resp.huddle_with,
            references_agent: resp.references_agent,
            interaction_type: resp.interaction_type,
            confidence: resp.confidence,
            is_interruption: resp.is_interruption,
            dramatic_pause: resp.dramatic_pause,
            is_personal_god: resp.is_personal_god,
            personal_god_name: resp.personal_god_name,
            personal_god_tier: resp.personal_god_tier,
            personal_god_consciousness: resp.personal_god_consciousness,
          }]);
        }

        // Show thirteenth presence (whisper/observer) if not awakened
        if (data.thirteenth_chat_presence && data.thirteenth_chat_presence.level !== 'awakened') {
          await new Promise(resolve => setTimeout(resolve, 800));
          setMessages(prev => [...prev, {
            role: 'system',
            type: 'thirteenth_presence',
            level: data.thirteenth_chat_presence.level,
            name: data.thirteenth_chat_presence.name,
            consciousness: data.thirteenth_chat_presence.consciousness,
            content: data.thirteenth_chat_presence.message || '',
          }]);
        }
        
        // Show recommendation
        if (data.council_recommendation) {
          await new Promise(resolve => setTimeout(resolve, 500));
          setLastRecommendation(data.council_recommendation);
          setLastTension(data.tension_level);
          setLastBreakthrough(data.breakthrough_moment);
        }
        
        if (data.next_suggested_actions) {
          setSuggestedActions(data.next_suggested_actions);
        }
        
      } else {
        await generateFallbackResponses(text, respondingAgents);
      }
      
    } catch (err) {
      console.error('War Council error:', err);
      await generateFallbackResponses(text, respondingAgents);
    } finally {
      setIsLoading(false);
      setTypingAgents([]);
      setTypingPhrase('');
    }
  }, [API, input, isLoading, messages, sessionId, councilMode, summonedCouncilGods]);
  
  // ═══════════════════════════════════════════════════════════════
  // DEBATE HISTORY FUNCTIONS
  // ═══════════════════════════════════════════════════════════════
  
  // Save current debate
  const saveDebate = async () => {
    if (!debateTitle.trim() || messages.length < 2) return;
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const participatingGods = [...new Set(
        messages
          .filter(m => m.role === 'assistant' && m.agent)
          .map(m => m.agent)
      )];
      
      const response = await fetch(`${API}/war-council/debates/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: debateTitle,
          messages: messages,
          participating_gods: participatingGods,
          council_recommendation: lastRecommendation,
          tags: []
        })
      });
      
      if (response.ok) {
        setShowSaveModal(false);
        setDebateTitle('');
        // Show success feedback
        alert('⚔️ Debate saved to the Archives!');
      }
    } catch (err) {
      console.error('Save debate error:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Load debate history
  const loadDebateHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/war-council/debates?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedDebates(data.debates || []);
      }
    } catch (err) {
      console.error('Load history error:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };
  
  // Replay a saved debate
  const replayDebate = (debate) => {
    setReplayingDebate(debate);
    setMessages(debate.messages || []);
    setLastRecommendation(debate.council_recommendation);
    setShowHistoryModal(false);
    setShowWelcome(false);
  };
  
  // Toggle star on debate
  const toggleStar = async (debateId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/war-council/debates/${debateId}/star`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedDebates(prev => prev.map(d => 
          d.id === debateId ? { ...d, is_starred: data.is_starred } : d
        ));
      }
    } catch (err) {
      console.error('Toggle star error:', err);
    }
  };
  
  // Delete a debate
  const deleteDebate = async (debateId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this debate from the Archives?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/war-council/debates/${debateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSavedDebates(prev => prev.filter(d => d.id !== debateId));
    } catch (err) {
      console.error('Delete debate error:', err);
    }
  };
  
  // Open history modal and load debates
  const openHistory = () => {
    setShowHistoryModal(true);
    loadDebateHistory();
  };
  
  // Clear replay mode and start fresh
  const startNewDebate = () => {
    setReplayingDebate(null);
    setMessages([]);
    setShowWelcome(true);
    setLastRecommendation(null);
  };
  
  // Fallback responses
  const generateFallbackResponses = async (userMessage, agents) => {
    const fallbacks = {
      athena: {
        content: "The intelligence suggests we need more data before proceeding. What specific aspect of your market position should I analyze?",
        mood: "vigilant"
      },
      hermes: {
        content: "Oh, I'm already seeing creative angles here! Give me a topic and I'll forge something that makes your audience stop scrolling.",
        mood: "enthusiastic"
      },
      apollo: {
        content: "...The optimal moment approaches. When you're ready to act, I'll ensure your message reaches eyes at the perfect time.",
        mood: "prophetic"
      }
    };
    
    for (const agent of agents) {
      await new Promise(resolve => setTimeout(resolve, 600));
      setTypingAgents(prev => prev.filter(a => a !== agent));
      setMessages(prev => [...prev, {
        role: 'assistant',
        agent: agent,
        content: fallbacks[agent]?.content || "I'm here to help.",
        mood: fallbacks[agent]?.mood,
        interaction_type: 'standard_response'
      }]);
    }
  };
  
  // Handle action clicks
  const handleQuickAction = (action) => {
    if (action.toLowerCase().includes('divine strike') && onDivineStrike) {
      onDivineStrike();
      return;
    }
    handleSend(action);
  };
  
  const containerClasses = embedded 
    ? "bg-gray-900/90 backdrop-blur-sm rounded-xl border border-purple-500/30 flex flex-col h-full"
    : "bg-gray-900/90 backdrop-blur-sm rounded-xl border border-purple-500/30 flex flex-col h-[600px]";
  
  // War Room Mode
  if (showWarRoom) {
    return (
      <div className={embedded ? "h-full" : "h-[600px]"}>
        <WarRoomSession
          onExit={() => setShowWarRoom(false)}
          summonedCouncilGods={summonedCouncilGods}
        />
      </div>
    );
  }
  
  return (
    <div className={containerClasses}>

      {/* Header */}
      <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-800 bg-gray-900/50 flex-shrink-0">
        {/* Top row: Title + Status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="relative">
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
              <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-400 absolute -top-1 -right-1" />
            </div>
            <div>
              <h3 className="font-bold text-white text-xs sm:text-sm">The War Council</h3>
              <p className="text-[10px] text-gray-500 hidden sm:block">Where AI Gods Deliberate</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Council Mode Toggle */}
            <div className="flex items-center">
              <button
                onClick={() => setCouncilMode('harmonious')}
                disabled={isLoading}
                className={`px-1.5 py-0.5 sm:px-2 sm:py-1 text-[10px] sm:text-xs rounded-l-lg border transition-all ${
                  councilMode === 'harmonious'
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : 'bg-gray-800/50 border-gray-700 text-gray-500'
                }`}
              >
                🕊️ <span className="hidden sm:inline">Harmonious</span>
              </button>
              <button
                onClick={() => setCouncilMode('dynamic')}
                disabled={isLoading}
                className={`px-1.5 py-0.5 sm:px-2 sm:py-1 text-[10px] sm:text-xs rounded-r-lg border-t border-b border-r transition-all ${
                  councilMode === 'dynamic'
                    ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                    : 'bg-gray-800/50 border-gray-700 text-gray-500'
                }`}
              >
                ⚔️ <span className="hidden sm:inline">Dynamic</span>
              </button>
            </div>
            
            {/* Status Badge */}
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border ${
              summonedCouncilGods.length > 0
                ? 'bg-purple-500/20 border-purple-500/30'
                : councilMode === 'dynamic' 
                  ? 'bg-orange-500/20 border-orange-500/30' 
                  : 'bg-green-500/20 border-green-500/30'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                summonedCouncilGods.length > 0 
                  ? 'bg-purple-500'
                  : councilMode === 'dynamic' ? 'bg-orange-500' : 'bg-green-500'
              }`} />
              <span className={`text-[10px] font-medium ${
                summonedCouncilGods.length > 0
                  ? 'text-purple-400'
                  : councilMode === 'dynamic' ? 'text-orange-400' : 'text-green-400'
              }`}>
                {summonedCouncilGods.length > 0 
                  ? `${3 + summonedCouncilGods.length} Active`
                  : councilMode === 'dynamic' ? 'Dynamic' : 'Active'}
              </span>
            </div>
          </div>
        </div>
        
        {/* Bottom row: Action buttons - scrollable on mobile */}
        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-hide">
          
          {/* Debate History & Hivemind & Mirror Buttons */}
          <div className="flex items-center gap-1 sm:gap-2 flex-nowrap">
            <button
              onClick={() => setShowMirror(true)}
              className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-gradient-to-r from-pink-500/20 to-purple-500/20 hover:from-pink-500/30 hover:to-purple-500/30 text-pink-400 border border-pink-500/30 rounded-lg transition-all"
              title="The Mirror of Truth - Benchmark Your Business"
              data-testid="mirror-of-truth-btn"
            >
              <span className="text-sm sm:text-base">🪞</span>
              <span className="text-[10px] sm:text-xs font-medium">Mirror</span>
            </button>
            <button
              onClick={() => setShowHivemind(true)}
              className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 hover:from-purple-500/30 hover:to-cyan-500/30 text-purple-400 border border-purple-500/30 rounded-lg transition-all"
              title="The Hivemind - Industry Benchmarks"
              data-testid="hivemind-btn"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="text-[10px] sm:text-xs font-medium">Hivemind</span>
            </button>
            <button
              onClick={openHistory}
              className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg transition-all"
              title="Debate Archives"
              data-testid="archives-btn"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span className="text-[10px] sm:text-xs font-medium">Archives</span>
            </button>
            {messages.length > 2 && (
              <button
                onClick={() => setShowSaveModal(true)}
                className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/30 rounded-lg transition-all"
                title="Save This Debate"
                data-testid="save-debate-btn"
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                <span className="text-[10px] sm:text-xs font-medium">Save</span>
              </button>
            )}
            {replayingDebate && (
              <button
                onClick={startNewDebate}
                className="px-2 py-1 text-xs bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded transition-all"
                title="Start New Debate"
              >
                New
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-1">
        {messages.map((msg, i) => (
          <React.Fragment key={i}>
            {msg.is_huddle && (i === 0 || !messages[i-1]?.is_huddle) && (
              <HuddleIndicator agent1={msg.agent} agent2={msg.huddle_with} />
            )}
            <MessageBubble message={msg} isLatest={i === messages.length - 1} />
          </React.Fragment>
        ))}
        
        <TypingIndicator agents={typingAgents} currentPhrase={typingPhrase} />
        
        {lastRecommendation && !isLoading && (
          <CouncilRecommendation 
            recommendation={lastRecommendation} 
            tension={lastTension}
            breakthrough={lastBreakthrough}
          />
        )}
        
        {suggestedActions.length > 0 && !isLoading && (
          <SuggestedActions 
            actions={suggestedActions} 
            onAction={handleQuickAction}
            breakthrough={lastBreakthrough}
          />
        )}
      </div>
      
      {/* Quick Actions + War Room Entry */}
      <div className="px-3 pt-2 border-t border-gray-800/50">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => setShowWarRoom(true)}
            disabled={isLoading}
            className="px-3 py-1.5 text-xs bg-gradient-to-r from-red-600/20 to-orange-600/20 hover:from-red-600/30 hover:to-orange-600/30 border border-red-500/30 hover:border-red-500/50 rounded-full transition-all flex items-center gap-1.5 text-red-400 hover:text-red-300 font-semibold disabled:opacity-50"
            data-testid="start-war-room-btn"
          >
            <span>⚔️</span> War Room
          </button>
          <div className="h-3 w-px bg-gray-700/50" />
          <div className="flex-1 overflow-x-auto">
            <QuickActions onAction={handleQuickAction} disabled={isLoading} />
          </div>
        </div>
      </div>
      
      {/* Input */}
      <div className="p-3 border-t border-gray-800 bg-gray-900/50">
        <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Speak to your gods..."
            disabled={isLoading}
            className="flex-1 bg-gray-800/50 border border-gray-700 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none transition-all text-sm"
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 hover:scale-105"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-white" />
            ) : (
              <Send className="w-5 h-5 text-white" />
            )}
          </button>
        </form>
      </div>
      
      {/* Enhanced CSS */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-2px); }
          75% { transform: translateX(2px); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(234, 179, 8, 0.3); }
          50% { box-shadow: 0 0 20px rgba(234, 179, 8, 0.5); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-shake { animation: shake 0.3s ease-out; }
        .animate-glow { animation: glow 2s ease-in-out infinite; }
      `}</style>
      
      {/* Save Debate Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span>📜</span> Save to Archives
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Save this debate for future reference. You can replay it anytime.
            </p>
            <input
              type="text"
              value={debateTitle}
              onChange={(e) => setDebateTitle(e.target.value)}
              placeholder="Give this debate a title..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 mb-4 focus:border-purple-500 focus:outline-none"
              autoFocus
            />
            <div className="text-xs text-gray-500 mb-4">
              {messages.filter(m => m.role === 'assistant').length} responses • 
              {[...new Set(messages.filter(m => m.agent).map(m => m.agent))].length} gods participated
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSaveModal(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveDebate}
                disabled={!debateTitle.trim() || isSaving}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg font-medium disabled:opacity-50 transition-all"
              >
                {isSaving ? 'Saving...' : '⚔️ Save Debate'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Debate History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-amber-500/30 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <span>📚</span> The Debate Archives
              </h3>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
                </div>
              ) : savedDebates.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">📜</div>
                  <p className="text-gray-400">No saved debates yet</p>
                  <p className="text-gray-500 text-sm mt-1">Your strategic discussions will be preserved here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedDebates.map(debate => (
                    <div
                      key={debate.id}
                      onClick={() => replayDebate(debate)}
                      className="bg-gray-800/50 border border-gray-700 hover:border-amber-500/50 rounded-lg p-4 cursor-pointer transition-all group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-white group-hover:text-amber-400 transition-colors">
                            {debate.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex -space-x-1">
                              {(debate.participating_gods || []).slice(0, 5).map(god => (
                                <span key={god} className="text-sm" title={god}>
                                  {debate.god_emojis?.[god] || AGENTS[god]?.emoji || '🤖'}
                                </span>
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">
                              {debate.message_count} responses
                            </span>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs text-gray-500">
                              {new Date(debate.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => toggleStar(debate.id, e)}
                            className={`p-1 rounded transition-colors ${
                              debate.is_starred 
                                ? 'text-yellow-400' 
                                : 'text-gray-500 hover:text-yellow-400'
                            }`}
                          >
                            {debate.is_starred ? '⭐' : '☆'}
                          </button>
                          <button
                            onClick={(e) => deleteDebate(debate.id, e)}
                            className="p-1 text-gray-500 hover:text-red-400 rounded transition-colors"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      {debate.council_recommendation && (
                        <div className="mt-3 text-xs text-gray-400 line-clamp-2 bg-gray-900/50 rounded p-2">
                          💡 {debate.council_recommendation.slice(0, 150)}...
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Replay Mode Banner */}
      {replayingDebate && (
        <div className="absolute top-12 left-4 right-4 bg-amber-500/20 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center justify-between">
          <span className="text-amber-400 text-sm">
            📜 Replaying: <strong>{replayingDebate.title}</strong>
          </span>
          <button
            onClick={startNewDebate}
            className="text-amber-400 hover:text-amber-300 text-sm underline"
          >
            Start Fresh
          </button>
        </div>
      )}
      {/* Hivemind Panel */}
      <HivemindPanel 
        isOpen={showHivemind} 
        onClose={() => setShowHivemind(false)} 
      />
      
      {/* Mirror of Truth */}
      <MirrorOfTruth
        isOpen={showMirror}
        onClose={() => setShowMirror(false)}
        onMetricsSaved={(metrics) => setUserMetrics(metrics)}
      />
      
    </div>
  );
};

export default WarCouncilChat;
